/* DC Cloth: the entrance banner as real hanging fabric.

   Replaces the flat <img> in .dc-hero__banner with a WebGL2 cloth that hangs off
   the truss beam and moves: gravity, wind, and a push where the pointer goes. The
   look follows JJ's Holocloth settings (black satin, base #101114, holo intensity
   0.10 at scale 8, band freq 0.20, spec tint 0.82, roughness 0.30, metalness 0.43,
   clearcoat 0.45, coat roughness 0.30, sheen 0.08), so the banner reads as printed
   satin rather than a picture of one.

   Self-contained on purpose: no three.js, no physics library. The hero already
   carries the Spline runtime for the hall and the theme has no 3D stack of its
   own, so this is raw WebGL2 plus a Verlet solver, about the size of one image.

   Falls back to the untouched <img> whenever WebGL2 is missing, the machine asks
   for reduced motion, or anything throws. The image stays in the DOM either way,
   so there is never a blank banner. */
(() => {
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__dcForceMotion;

  /* Grid, sized to the device. The solver is the most expensive thing on this page: at
     the desktop grid it is 1280 points and roughly 39,000 constraint solves per step, up
     to three steps a frame, in plain JS. Measured on a phone viewport that was the ONLY
     thing running through the hero and it owned every frame spike over 30 ms — no Spline
     scene was even live at those scroll positions.

     A phone renders this banner a few hundred pixels wide, where a 40 x 14 sheet is
     indistinguishable from a 64 x 20 one, so the grid, the relaxation passes and the
     catch-up cap all come down there. That is about a quarter of the work for the same
     picture. */
  const PHONE = matchMedia('(max-width: 767px)').matches;
  const NX = PHONE ? 40 : 64, NY = PHONE ? 14 : 20;
  const ITER = PHONE ? 3 : 5;  // constraint relaxation passes per step
  const HEM_ITER = 2;          // extra passes on the rope hem, per relaxation pass
  const STEP = 1 / 60;         // fixed physics step
  const MAX_STEPS = PHONE ? 2 : 3;  // catch-up cap after a stall
  const GRAVITY = -150;        // light: the sheet is strapped, not hanging
  const DAMP = 0.986;
  const WIND = 900;            // gust strength, ripples the belly
  /* Vertical slack in the printed area. The rope hems are taut but the fabric
     between them is cut a little long, which is why a real banner bellies out
     between its ties instead of sitting like a board. Rest lengths are measured off
     a reference grid this much taller than the sheet, so anything with vertical
     extent gets the slack and anything purely horizontal gets none: the belly can
     swing about 45 u deep while the print keeps its width to the pixel. */
  const VSLACK = 1.07;
  /* Pull back toward the flat plane. A banner this size only bows so far before the
     hem takes the load, and depth is what wrecks the print: every unit the belly
     travels in z is a unit of width the sheet has to borrow from somewhere, which
     is what was squashing DIECAST SHOW into itself. This caps the bow near 60 u,
     where the horizontal loss is under one percent. */
  const PLANE_K = 6;
  const POINTER_R = 190;       // pointer push radius, plane units
  const POINTER_F = 1500;

  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

  const VERT = `#version 300 es
  precision highp float;
  in vec3 aPos;
  in vec3 aNrm;
  in vec2 aUv;
  uniform mat4 uProj;
  out vec3 vN;
  out vec2 vUv;
  out vec3 vP;
  void main() {
    vN = aNrm;
    vUv = aUv;
    vP = aPos;
    gl_Position = uProj * vec4(aPos, 1.0);
  }`;

  /* Satin: a soft GGX lobe for the sheet sheen, a Fresnel rim so the folds catch
     light, a clearcoat highlight on top, and a faint iridescent band that follows
     the surface angle. The artwork is printed into the albedo, so it darkens and
     brightens with the folds exactly like ink on fabric. */
  const FRAG = `#version 300 es
  precision highp float;
  in vec3 vN;
  in vec2 vUv;
  in vec3 vP;
  out vec4 outColor;

  uniform sampler2D uArt;
  uniform vec3 uBase;
  uniform float uTime;
  uniform float uHoloIntensity;
  uniform float uHoloScale;
  uniform float uBandFreq;
  uniform float uSpecTint;
  uniform float uRough;
  uniform float uMetal;
  uniform float uCoat;
  uniform float uCoatRough;
  uniform float uSheen;

  const vec3 L1 = normalize(vec3(-0.35, 0.55, 0.75));   // key, the stage lamps above
  const vec3 L2 = normalize(vec3(0.55, -0.25, 0.60));   // bounce off the floor
  const vec3 V  = vec3(0.0, 0.0, 1.0);

  /* GGX normal distribution, normalised so the lobe peaks at 1 instead of
     1/(pi*a*a). At roughness 0.3 the raw NDF peaks near 39, so any patch of cloth
     whose normal bisected the light and the eye blew out to flat white and took the
     printed artwork with it. Same shape, sane range. */
  float ggx(vec3 n, vec3 l, float rough) {
    vec3 h = normalize(l + V);
    float a = max(rough * rough, 0.002);
    float ndh = max(dot(n, h), 0.0);
    float d = (ndh * ndh) * (a * a - 1.0) + 1.0;
    float a2 = a * a;
    return (a2 * a2) / max(d * d, 1e-6);
  }

  void main() {
    vec3 n = normalize(vN);
    if (!gl_FrontFacing) n = -n;

    vec4 art = texture(uArt, vUv);
    // Ink sits on the cloth: where the art is opaque it replaces the base tone.
    vec3 albedo = mix(uBase, art.rgb, art.a);

    float ndl1 = max(dot(n, L1), 0.0);
    float ndl2 = max(dot(n, L2), 0.0);
    float diff = 0.30 + 0.72 * ndl1 + 0.22 * ndl2;

    float fres = pow(1.0 - max(dot(n, V), 0.0), 3.0);

    // Specular, tinted toward the albedo as metalness rises
    vec3 specTint = mix(vec3(1.0), albedo, uSpecTint * uMetal);
    // Weighted by incidence, so a highlight can only appear where light lands
    float spec = ggx(n, L1, uRough) * ndl1 * 0.9 + ggx(n, L2, uRough) * ndl2 * 0.35;
    // Clearcoat: a tighter second lobe over the top
    float coat = ggx(n, L1, uCoatRough) * ndl1 * uCoat;

    // Sheen: the soft edge glow that makes fabric read as fabric
    vec3 sheen = vec3(uSheen * 3.2) * fres;

    // Holographic banding, driven by the surface angle so it travels with folds
    float band = sin((n.x * uHoloScale + n.y * uHoloScale * 0.6 + vP.x * uBandFreq * 0.01) + uTime * 0.35);
    vec3 holo = vec3(
      0.5 + 0.5 * sin(band * 2.0 + 0.0),
      0.5 + 0.5 * sin(band * 2.0 + 2.1),
      0.5 + 0.5 * sin(band * 2.0 + 4.2)
    );
    vec3 holoAdd = holo * uHoloIntensity * fres * 1.4;

    vec3 col = albedo * diff + specTint * spec * 0.55 + vec3(coat) * 0.35 + sheen + holoAdd;
    // Keep the printed red punchy against all that shading
    col = mix(col, col * 1.06, art.a);
    /* Soft shoulder above 0.82 so a hot fold rolls off toward white and keeps its
       gradient, rather than clipping to a flat patch that hides whatever is
       printed underneath it. Approaches 1.0 and never crosses it. */
    vec3 over = max(col - 0.82, vec3(0.0));
    col = min(col, vec3(0.82)) + 0.18 * over / (0.18 + over);
    outColor = vec4(col, 1.0);
  }`;

  function compile(gl, type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader');
    return s;
  }

  function program(gl) {
    const p = gl.createProgram();
    gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || 'link');
    return p;
  }

  /* ---- Verlet cloth ------------------------------------------------------- */
  class Cloth {
    constructor(w, h) {
      this.w = w; this.h = h;
      const n = NX * NY;
      this.pos = new Float32Array(n * 3);
      this.prev = new Float32Array(n * 3);
      this.pin = new Uint8Array(n);
      this.nrm = new Float32Array(n * 3);
      this.uv = new Float32Array(n * 2);
      for (let y = 0; y < NY; y++) {
        for (let x = 0; x < NX; x++) {
          const i = y * NX + x, i3 = i * 3;
          const px = (x / (NX - 1) - 0.5) * w;
          const py = -(y / (NY - 1)) * h;
          this.pos[i3] = px; this.pos[i3 + 1] = py; this.pos[i3 + 2] = 0;
          this.prev[i3] = px; this.prev[i3 + 1] = py; this.prev[i3 + 2] = 0;
          this.uv[i * 2] = x / (NX - 1);
          this.uv[i * 2 + 1] = y / (NY - 1);
        }
      }
      /* Strapped at all four corners, which is how this banner is actually rigged
         (JJ 2026-09-03). The middle is left free, so the wind billows the belly of
         the sheet instead of swinging the whole thing off the top edge. */
      const TIE = 2;
      const last = (NY - 1) * NX;
      for (let x = 0; x < TIE; x++) {
        this.pin[x] = 1;                    // top left
        this.pin[NX - 1 - x] = 1;           // top right
        this.pin[last + x] = 1;             // bottom left
        this.pin[last + NX - 1 - x] = 1;    // bottom right
      }

      this.cons = [];
      const add = (a, b) => {
        const a3 = a * 3, b3 = b * 3;
        const dx = this.pos[a3] - this.pos[b3];
        const dy = (this.pos[a3 + 1] - this.pos[b3 + 1]) * VSLACK;
        this.cons.push(a, b, Math.hypot(dx, dy));
      };
      for (let y = 0; y < NY; y++) {
        for (let x = 0; x < NX; x++) {
          const i = y * NX + x;
          if (x < NX - 1) add(i, i + 1);                 // structural
          if (y < NY - 1) add(i, i + NX);
          if (x < NX - 1 && y < NY - 1) add(i, i + NX + 1); // shear
          if (x > 0 && y < NY - 1) add(i, i + NX - 1);
          if (x < NX - 2) add(i, i + 2);                 // bend, keeps it from folding flat
          if (y < NY - 2) add(i, i + NX * 2);
        }
      }
      this.consArr = new Float32Array(this.cons);

      /* The hem. A real banner has rope threaded through all four edges and is tied
         off at the corners, which is why the corners are the only pins: the rope,
         not the fabric, carries the span. Solving the perimeter separately and more
         often makes those four edges effectively inextensible, so the top edge stays
         a straight line instead of sagging into a catenary and the print keeps its
         rectangle while the middle is free to move. */
      this.hem = [];
      const addHem = (a, b) => {
        const a3 = a * 3, b3 = b * 3;
        this.hem.push(a, b, Math.hypot(this.pos[a3] - this.pos[b3], this.pos[a3 + 1] - this.pos[b3 + 1]));
      };
      for (let x = 0; x < NX - 1; x++) {
        addHem(x, x + 1);                                    // top
        addHem(last + x, last + x + 1);                      // bottom
      }
      for (let y = 0; y < NY - 1; y++) {
        addHem(y * NX, (y + 1) * NX);                        // left
        addHem(y * NX + NX - 1, (y + 1) * NX + NX - 1);      // right
      }
      this.hemArr = new Float32Array(this.hem);

      this.idx = new Uint16Array((NX - 1) * (NY - 1) * 6);
      let k = 0;
      for (let y = 0; y < NY - 1; y++) {
        for (let x = 0; x < NX - 1; x++) {
          const i = y * NX + x;
          this.idx[k++] = i; this.idx[k++] = i + NX; this.idx[k++] = i + 1;
          this.idx[k++] = i + 1; this.idx[k++] = i + NX; this.idx[k++] = i + NX + 1;
        }
      }
      this.t = 0;
      this.pointer = null;
    }

    step(dt) {
      this.t += dt;
      const { pos, prev, pin } = this;
      const n = NX * NY;
      const t = this.t;
      for (let i = 0; i < n; i++) {
        if (pin[i]) continue;
        const i3 = i * 3;
        const x = pos[i3], y = pos[i3 + 1], z = pos[i3 + 2];
        // Wind: two travelling waves plus a slow swell, strongest near the hem
        const depth = 1 - (y / -this.h);
        const sway = 1 - depth;
        /* Waves run DOWN the sheet, not across it. Depth that varies along x is
           depth the sheet pays for in width, which is what was folding DIECAST SHOW
           into itself; depth that varies along y costs the print nothing. So the
           short wavelengths are all in y and x carries only one long, lazy swell. */
        const wind =
          Math.sin(t * 1.30 + y * 0.030 + x * 0.0022) * 0.55 +
          Math.sin(t * 0.62 - x * 0.0018) * 0.45 +
          Math.sin(t * 2.10 + y * 0.055) * 0.18;
        let ax = Math.sin(t * 0.6 + y * 0.004) * WIND * 0.10 * sway;
        let ay = GRAVITY;
        let az = wind * WIND * (0.18 + 0.95 * sway) - z * PLANE_K;

        if (this.pointer) {
          const dx = x - this.pointer.x, dy = y - this.pointer.y;
          const d = Math.hypot(dx, dy);
          if (d < POINTER_R) {
            const f = (1 - d / POINTER_R) * POINTER_F;
            ax += (dx / (d || 1)) * f * 0.35;
            ay += (dy / (d || 1)) * f * 0.35;
            az -= f;
          }
        }

        const nx = x + (x - prev[i3]) * DAMP + ax * dt * dt;
        const ny = y + (y - prev[i3 + 1]) * DAMP + ay * dt * dt;
        const nz = z + (z - prev[i3 + 2]) * DAMP + az * dt * dt;
        prev[i3] = x; prev[i3 + 1] = y; prev[i3 + 2] = z;
        pos[i3] = nx; pos[i3 + 1] = ny; pos[i3 + 2] = nz;
      }

      const relax = (c) => {
        for (let i = 0; i < c.length; i += 3) {
          const a3 = c[i] * 3, b3 = c[i + 1] * 3, rest = c[i + 2];
          const dx = pos[b3] - pos[a3];
          const dy = pos[b3 + 1] - pos[a3 + 1];
          const dz = pos[b3 + 2] - pos[a3 + 2];
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-4;
          const diff = ((d - rest) / d) * 0.5;
          const ox = dx * diff, oy = dy * diff, oz = dz * diff;
          const pa = pin[c[i]], pb = pin[c[i + 1]];
          if (!pa) { pos[a3] += ox; pos[a3 + 1] += oy; pos[a3 + 2] += oz; }
          if (!pb) { pos[b3] -= ox; pos[b3 + 1] -= oy; pos[b3 + 2] -= oz; }
        }
      };
      const body = this.consArr;
      const hem = this.hemArr;
      for (let k = 0; k < ITER; k++) {
        relax(body);
        for (let j = 0; j < HEM_ITER; j++) relax(hem);
      }
    }

    normals() {
      const { pos, nrm } = this;
      nrm.fill(0);
      const idx = this.idx;
      for (let i = 0; i < idx.length; i += 3) {
        const a = idx[i] * 3, b = idx[i + 1] * 3, c = idx[i + 2] * 3;
        const ux = pos[b] - pos[a], uy = pos[b + 1] - pos[a + 1], uz = pos[b + 2] - pos[a + 2];
        const vx = pos[c] - pos[a], vy = pos[c + 1] - pos[a + 1], vz = pos[c + 2] - pos[a + 2];
        const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
        nrm[a] += nx; nrm[a + 1] += ny; nrm[a + 2] += nz;
        nrm[b] += nx; nrm[b + 1] += ny; nrm[b + 2] += nz;
        nrm[c] += nx; nrm[c + 1] += ny; nrm[c + 2] += nz;
      }
      for (let i = 0; i < nrm.length; i += 3) {
        const l = Math.hypot(nrm[i], nrm[i + 1], nrm[i + 2]) || 1;
        nrm[i] /= l; nrm[i + 1] /= l; nrm[i + 2] /= l;
      }
    }
  }

  /* ---- mount -------------------------------------------------------------- */
  function mount(host, img) {
    const W = 1000, H = 300;            // the plane's own units, from the section
    const gl0 = document.createElement('canvas');
    const gl = gl0.getContext('webgl2', { alpha: true, antialias: true, premultipliedAlpha: false });
    if (!gl) return null;

    gl0.className = 'dc-hero__banner-cloth';
    gl0.setAttribute('aria-hidden', 'true');
    host.appendChild(gl0);

    const prog = program(gl);
    gl.useProgram(prog);
    const cloth = new Cloth(W, H);
    cloth.normals();

    const buf = {
      pos: gl.createBuffer(), nrm: gl.createBuffer(), uv: gl.createBuffer(), idx: gl.createBuffer(),
    };
    const loc = {
      pos: gl.getAttribLocation(prog, 'aPos'),
      nrm: gl.getAttribLocation(prog, 'aNrm'),
      uv: gl.getAttribLocation(prog, 'aUv'),
    };
    const u = (n) => gl.getUniformLocation(prog, n);
    const U = {
      proj: u('uProj'), art: u('uArt'), base: u('uBase'), time: u('uTime'),
      holoI: u('uHoloIntensity'), holoS: u('uHoloScale'), band: u('uBandFreq'),
      specTint: u('uSpecTint'), rough: u('uRough'), metal: u('uMetal'),
      coat: u('uCoat'), coatRough: u('uCoatRough'), sheen: u('uSheen'),
    };

    gl.bindBuffer(gl.ARRAY_BUFFER, buf.uv);
    gl.bufferData(gl.ARRAY_BUFFER, cloth.uv, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(loc.uv);
    gl.vertexAttribPointer(loc.uv, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buf.idx);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, cloth.idx, gl.STATIC_DRAW);

    // Artwork, straight off the <img> the section already loads
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const upload = () => {
      try {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      } catch (e) { /* tainted or not ready: the base cloth still renders */ }
    };
    if (img.complete && img.naturalWidth) upload();
    else img.addEventListener('load', upload, { once: true });

    /* Orthographic, matching the plane exactly, with headroom on Z so folds that
       swing toward the camera are not clipped. Keeps the cloth registered with the
       CSS 3D layout the rest of the hero uses. */
    const pad = 40;
    const proj = new Float32Array([
      2 / (W + pad * 2), 0, 0, 0,
      0, 2 / (H + pad * 2), 0, 0,
      0, 0, -1 / 1200, 0,
      0, (H / 2 + pad) * (2 / (H + pad * 2)) - 1 + (pad / (H + pad * 2)), 0, 1,
    ]);
    // Simple ortho: x in [-W/2-pad, W/2+pad], y in [-H-pad, pad]
    proj[0] = 2 / (W + pad * 2);
    proj[5] = 2 / (H + pad * 2);
    proj[10] = -1 / 1200;
    proj[12] = 0;
    proj[13] = 1 - (2 * pad) / (H + pad * 2);
    proj[14] = 0;

    gl.uniformMatrix4fv(U.proj, false, proj);
    gl.uniform3f(U.base, 0x10 / 255, 0x11 / 255, 0x14 / 255);
    gl.uniform1f(U.holoI, 0.10);
    gl.uniform1f(U.holoS, 8.0);
    gl.uniform1f(U.band, 0.20);
    gl.uniform1f(U.specTint, 0.82);
    gl.uniform1f(U.rough, 0.30);
    gl.uniform1f(U.metal, 0.43);
    gl.uniform1f(U.coat, 0.45);
    gl.uniform1f(U.coatRough, 0.30);
    gl.uniform1f(U.sheen, 0.08);
    gl.uniform1i(U.art, 0);

    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const resize = () => {
      const r = host.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(2, Math.round(r.width * dpr));
      const h = Math.max(2, Math.round(r.height * dpr));
      if (gl0.width !== w || gl0.height !== h) { gl0.width = w; gl0.height = h; }
      gl.viewport(0, 0, gl0.width, gl0.height);
    };
    resize();
    const ro = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    if (ro) ro.observe(host);
    window.addEventListener('resize', resize);

    // Pointer: map the page pointer into plane units
    const onMove = (e) => {
      const r = host.getBoundingClientRect();
      if (!r.width) return;
      cloth.pointer = {
        x: ((e.clientX - r.left) / r.width - 0.5) * W,
        y: -((e.clientY - r.top) / r.height) * H,
      };
    };
    const onLeave = () => { cloth.pointer = null; };
    host.addEventListener('pointermove', onMove);
    host.addEventListener('pointerleave', onLeave);

    let raf = 0, last = performance.now(), acc = 0, alive = true, visible = true;
    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver((es) => { visible = es.some((x) => x.isIntersecting); }, { threshold: 0.01 })
      : null;
    if (io) io.observe(host);

    const frame = (now) => {
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      const dt = Math.min((now - last) / 1000, 0.25);
      last = now;
      if (!visible) return;
      acc += dt;
      let steps = 0;
      while (acc >= STEP && steps < MAX_STEPS) { cloth.step(STEP); acc -= STEP; steps++; }
      if (acc > STEP * MAX_STEPS) acc = 0;
      cloth.normals();

      gl.bindBuffer(gl.ARRAY_BUFFER, buf.pos);
      gl.bufferData(gl.ARRAY_BUFFER, cloth.pos, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(loc.pos);
      gl.vertexAttribPointer(loc.pos, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf.nrm);
      gl.bufferData(gl.ARRAY_BUFFER, cloth.nrm, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(loc.nrm);
      gl.vertexAttribPointer(loc.nrm, 3, gl.FLOAT, false, 0, 0);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.uniform1f(U.time, cloth.t);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.drawElements(gl.TRIANGLES, cloth.idx.length, gl.UNSIGNED_SHORT, 0);
    };
    raf = requestAnimationFrame(frame);

    host.classList.add('is-cloth');
    return {
      destroy() {
        alive = false;
        cancelAnimationFrame(raf);
        try { ro && ro.disconnect(); } catch (e) {}
        try { io && io.disconnect(); } catch (e) {}
        window.removeEventListener('resize', resize);
        host.removeEventListener('pointermove', onMove);
        host.removeEventListener('pointerleave', onLeave);
        host.classList.remove('is-cloth');
        gl0.remove();
      },
    };
  }

  function init() {
    if (REDUCED) return;
    document.querySelectorAll('.dc-hero__banner').forEach((host) => {
      if (host.__dcCloth) return;
      const img = host.querySelector('img');
      if (!img) return;
      try {
        const inst = mount(host, img);
        if (inst) host.__dcCloth = inst;
      } catch (e) {
        /* Any failure leaves the flat banner exactly as it was. */
        if (host.__dcCloth) { try { host.__dcCloth.destroy(); } catch (e2) {} host.__dcCloth = null; }
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
